import React, { Fragment, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Row, Col, Card, Button } from 'reactstrap';
import AdminHeader from '../../../components/header/AdminHeader';
import FormSelect from '../../../components/form/FormSelect';
import { refreshTimeOptions } from '../../../utils/common';
import AiReportCount from './AiReportCount';
import AiReportList from './AiReportList';
import AiTranscriptModal from './AiTranscriptModal';
import ProcessDetails from './ProcessDetails';
import ClearBufferConfirmationModal from './ClearBufferConfirmationModal';
import HangupConfirmationModal from './HangupConfirmationModal';
import { toast } from 'react-toastify';
import {
  aiProcessListGet,
  aiCountGet,
  aiListGet,
  aiTranscriptGet,
  aiClearBuffer,
  aiHangupCall,
  clearAiTranscript,
  useAiProcesses,
  useAiDashboard,
  useAiActiveCalls,
  useAiTranscript
} from '../../../store/slices/tenantSlices/aiRealtimeReportSlice';

const AiRealtimeReport = () => {
  const dispatch = useDispatch();
  const processes = useAiProcesses();
  const dashboardData = useAiDashboard();
  const activeCalls = useAiActiveCalls();
  const transcriptData = useAiTranscript();

  const [refreshTime, setRefreshTime] = useState(5);
  const [transcriptModalOpen, setTranscriptModalOpen] = useState(false);
  const [bufferClearModalOpen, setBufferClearModalOpen] = useState(false);
  const [selectedCall, setSelectedCall] = useState(null);
  const [transcriptLoading, setTranscriptLoading] = useState(false);
  const [hangupSession, setHangupSession] = useState(null);
  const [hangupLoading, setHangupLoading] = useState(false);

  const fetchProcessList = async () => {
    try {
      await dispatch(aiProcessListGet()).unwrap();
    } catch (error) {
      console.error('Error fetching process list:', error);
    }
  };

  const fetchData = async () => {
    try {
      const payload = {};
      await dispatch(aiCountGet(payload)).unwrap();
      await dispatch(aiListGet(payload)).unwrap();
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    }
  };

  useEffect(() => {
    fetchProcessList();
  }, []);

  useEffect(() => {
    fetchData();
    if (refreshTime) {
      const intervalId = setInterval(fetchData, refreshTime * 1000);
      return () => clearInterval(intervalId);
    }
  }, [refreshTime]);

  const handleTranscriptClick = (call) => {
    setSelectedCall(call);
    setTranscriptModalOpen(true);
    fetchTranscript(call.session_id);
  };

  const fetchTranscript = async (id) => {
    setTranscriptLoading(true);
    try {
      await dispatch(aiTranscriptGet(id)).unwrap();
    } catch (error) {
      console.error('Failed to fetch transcript:', error);
    } finally {
      setTranscriptLoading(false);
    }
  };

  const handleTranscriptClose = () => {
    setTranscriptModalOpen(false);
    setSelectedCall(null);
    dispatch(clearAiTranscript());
  };

  const handleTranscriptRefresh = () => {
    if (selectedCall) {
      fetchTranscript(selectedCall.session_id);
    }
  };

  const onBufferClear = async () => {
    try {
      const response = await dispatch(aiClearBuffer()).unwrap();
      toast.success(response?.message);
      setBufferClearModalOpen(false);
    } catch (error) {
      console.error('Error clearing buffer:', error);
    }
  };

  const onHangup = async () => {
    if (!hangupSession) return;
    setHangupLoading(true);
    try {
      const response = await dispatch(aiHangupCall(hangupSession)).unwrap();
      toast.success(response?.message || 'Call hung up successfully');
      setHangupSession(null);
      fetchData();
    } catch (error) {
      console.error('Error hanging up call:', error);
    } finally {
      setHangupLoading(false);
    }
  };

  return (
    <Fragment>
      <AdminHeader title="AI Realtime Report" />
      {/* Row 1: full-width dashboard summary cards */}
      <Row>
        <Col lg="12" md="12" className="px-2 mb-2">
          <AiReportCount data={dashboardData} />
        </Col>
      </Row>

      {/* Row 2: process sidebar | live calls list | controls */}
      <Row>
        <Col lg="2" md="2" className="px-2">
          <Card className="mb-3 px-2 py-2">
            <Row>
              <Col lg="12" md="12" className="mb-2">
                <FormSelect
                  isShowLabel={false}
                  name="refreshTime"
                  value={refreshTime}
                  placeholder="Select Refresh"
                  options={refreshTimeOptions}
                  onChange={(e) => setRefreshTime(Number(e.target.value))}
                />
              </Col>
              <Col lg="12" md="12">
                <Button
                  color="primary"
                  className="px-4 w-100"
                  type="submit"
                  onClick={() => setBufferClearModalOpen(true)}>
                  Clear Buffer
                </Button>
              </Col>
            </Row>
          </Card>
          <Card className="m-0 ai-process-details">
            <ProcessDetails data={processes} isClickable={false} />
          </Card>
        </Col>

        <Col lg="10" md="10" className="px-2">
          <Card className="m-0">
            <AiReportList
              data={activeCalls}
              onTranscriptClick={handleTranscriptClick}
              onHangupClick={(row) => setHangupSession(row.session_id)}
            />
          </Card>
        </Col>
      </Row>

      <AiTranscriptModal
        isOpen={transcriptModalOpen}
        onClose={handleTranscriptClose}
        data={transcriptData}
        isLoading={transcriptLoading}
        onRefresh={handleTranscriptRefresh}
      />

      <ClearBufferConfirmationModal
        isOpen={bufferClearModalOpen}
        onClearClick={() => onBufferClear()}
        onCloseClick={() => setBufferClearModalOpen(false)}
      />

      <HangupConfirmationModal
        isOpen={!!hangupSession}
        onHangupClick={() => onHangup()}
        onCloseClick={() => setHangupSession(null)}
        isLoading={hangupLoading}
      />
    </Fragment>
  );
};

export default AiRealtimeReport;
